import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import AnimatedAtaric from '../shared/AnimatedAtaric';
import analytics from '../../libs/analytics';
import './onboarding.css';

const STORAGE_KEY = 'ataric_onboarding_seen';

interface Slide {
  emoji: string;
  title: string;
  description: string;
}

const slides: Slide[] = [
  {
    emoji: '👻',
    title: 'Bem-vindo ao ATARIC!',
    description: 'Seu parceiro de inglês com IA. Conheça tudo que você pode fazer por aqui.',
  },
  {
    emoji: '💬',
    title: 'Converse em inglês',
    description: 'Pratique conversas reais com nossa IA. Escolha entre modo Iniciante ou Avançado e comece a falar agora.',
  },
  {
    emoji: '🎮',
    title: 'Ataric Land',
    description: 'Um jogo imersivo e interativo para aprender inglês de verdade. Complete desafios, explore fases e evolua enquanto se diverte.',
  },
  {
    emoji: '🗺️',
    title: 'Mapas Mentais',
    description: 'Expanda seu vocabulário com mapas mentais interativos organizados por temas do inglês.',
  },
  {
    emoji: '📈',
    title: 'Acompanhe seu Progresso',
    description: 'Veja suas estatísticas, desbloqueie conquistas e acompanhe sua evolução semana a semana.',
  },
];

const slideVariants = {
  enter: (dir: number) => ({ x: dir * 50, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir * -50, opacity: 0 }),
};

function OnboardingModal() {
  const [isOpen] = useState(() => !localStorage.getItem(STORAGE_KEY));
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (isOpen && !closed) {
      analytics.onboardingViewed();
    }
  }, []);

  function close() {
    localStorage.setItem(STORAGE_KEY, 'true');
    if (currentSlide === slides.length - 1) {
      analytics.onboardingCompleted();
    } else {
      analytics.onboardingSkipped(currentSlide);
    }
    setClosed(true);
  }

  function goNext() {
    if (currentSlide < slides.length - 1) {
      setDirection(1);
      const nextSlide = currentSlide + 1;
      analytics.onboardingSlideViewed(nextSlide, slides[nextSlide].title);
      setCurrentSlide(p => p + 1);
    } else {
      analytics.onboardingCompleted();
      localStorage.setItem(STORAGE_KEY, 'true');
      setClosed(true);
    }
  }

  function goPrev() {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(p => p - 1);
    }
  }

  function goTo(index: number) {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  }

  const slide = slides[currentSlide];
  const isLast = currentSlide === slides.length - 1;
  const isFirst = currentSlide === 0;

  return (
    <AnimatePresence>
      {isOpen && !closed && (
        <motion.div
          className="onboarding-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
        >
          <motion.div
            className="onboarding-modal"
            initial={{ scale: 0.92, opacity: 0, y: 24 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 24 }}
            transition={{ type: 'spring', damping: 20, stiffness: 260 }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {/* Close */}
            <button className="onboarding-close" onClick={close} aria-label="Fechar">
              ×
            </button>

            {/* Slide area */}
            <div className="onboarding-slide-area">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentSlide}
                  className="onboarding-slide"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.22, ease: 'easeInOut' }}
                >
                  {/* Icon */}
                  <div className="onboarding-icon">
                    {isFirst ? (
                      <AnimatedAtaric
                        size="large"
                        behavior="wave"
                        autoAnimate={false}
                        showMessages={false}
                      />
                    ) : (
                      <div className="onboarding-emoji-icon">{slide.emoji}</div>
                    )}
                  </div>

                  <h2 className="onboarding-title">{slide.title}</h2>
                  <p className="onboarding-description">{slide.description}</p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Dot indicators */}
            <div className="onboarding-dots">
              {slides.map((_, i) => (
                <button
                  key={i}
                  className={`onboarding-dot${i === currentSlide ? ' onboarding-dot--active' : ''}`}
                  onClick={() => goTo(i)}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className="onboarding-actions">
              {!isFirst && (
                <button className="onboarding-btn-secondary" onClick={goPrev}>
                  Anterior
                </button>
              )}
              <button
                className="onboarding-btn-primary"
                onClick={goNext}
                style={isFirst ? { width: '100%' } : {}}
              >
                {isLast ? 'Começar agora!' : 'Próximo'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default OnboardingModal;
